<template>
  <n-auth-panel>
    <template v-slot:heading>
      <n-html :text="$t('auth.login.title')" />
    </template>

    <template v-slot:content>
      <n-html :text="$t('auth.login.subtitle')" />
    </template>

    <div class="flex flex-col gap-y-6">
      <div class="flex flex-col gap-6">
        <div class="space-y-8">
          <n-form-group
            id="input-group-email"
            label-for="input-email"
            :label="$t('form.email.label')"
            :invalid-feedback="$t('form.email.feedback.invalid')"
            :state="form_states.email"
          >
            <n-form-input-default
              id="input-email"
              :placeholder="$t('form.email.placeholder')"
              v-model="email"
              type="email"
            />
          </n-form-group>

          <n-form-group
            v-show="!passwordless_login"
            id="input-group-password"
            :label="$t('form.password.label')"
            label-for="input-password"
            :invalid-feedback="$t('form.password.feedback.invalid')"
            :state="form_states.password"
          >
            <div class="flex flex-col gap-y-2">
              <n-form-input-password
                id="input-password"
                :placeholder="$t('form.password.placeholder')"
                v-model="password"
                v-on:keyup.enter.native="login"
              />
              <a
                class="text-sm text-brand-purple-500 underline cursor-pointer text-right flex justify-end"
                @click="$router.push({ name: 'auth.forgot_password' })"
              >
                <n-html :text="$t('auth.login.password_forgot')" />
              </a>
            </div>
          </n-form-group>
        </div>

        <div
          v-show="has_error || send_email"
          class="flex flex-col font-base text-xs space-y-2"
        >
          <p v-show="has_error" class="text-brand-red-500">
            <n-html :text="getErrorMessage()" />
          </p>
          <p v-show="send_email" class="">
            <n-html :text="$t('auth.login.sent_login_link')" />
          </p>
        </div>

        <n-form-group
          id="button-group-login"
          :invalid-feedback="$t('auth.login.error')"
          :state="form_states.login"
        >
          <n-button-regular full-width @click.native="login">
            <n-html :text="$t('auth.login.button')" />
          </n-button-regular>
        </n-form-group>
      </div>

      <seperator>
        <n-html :text="$t('form.easy_email.or')" />
      </seperator>

      <div class="flex gap-4 flex-col">
        <n-button-regular
          tertairy
          full-width
          @click.native="changeLoginMethod"
        >
          <n-html
            :text="
              passwordless_login
                ? $t('auth.login.deactivate_passwordless_login')
                : $t('auth.login.activate_passwordless_login')
            "
          />
        </n-button-regular>

        <n-button-regular
          tertairy
          full-width
          @click.native="$router.push({ name: 'auth.register' })"
        >
          <n-html :text="$t('auth.login.register')" />
        </n-button-regular>
      </div>
    </div>
  </n-auth-panel>
</template>

<script>
import validation from "../../mixins/validation";
import Seperator from "@/components/theme/form/seperator.vue";

export default {
  name: "pages.auth.login",
  components: { Seperator },
  mixin: [validation],
  data: () => ({
    email: "",
    password: "",
    passwordless_login: false,
    send_email: false,
    form_states: {
      email: null,
      password: null,
      login: null
    },
    has_error: false,
    errors: {
      login_error: "",
      error: false,
      incorrect_passwordless_email: false,
      invalid_token: false
    }
  }),
  computed: {
    customToken() {
      return this.$route.query.token;
    },
    valid() {
      return (
        !!(this.email && this.password) & this.validateEmail(this.email) &&
        this.validatePassword(this.password)
      );
    }
  },
  created() {
    if (this.customToken) {
      this.loginWithCustomToken();
    }

    if (this.$firebase.auth().isSignInWithEmailLink(window.location.href)) {
      this.loginWithEmailLink();
    }
  },
  methods: {
    resetErrors() {
      this.errors.login_error = "";
      this.errors.error = false;
      this.errors.incorrect_passwordless_email = false;
      this.errors.invalid_token = false;
    },
    changeLoginMethod() {
      this.resetErrors();

      this.passwordless_login = !this.passwordless_login;
    },
    checkUserExists() {
      // Used for password login error handling
      this.$store
        .dispatch("application/userExists", this.email)
        .then(response => {
          if (response.data.allowed) {
            this.errors.login_error = "PASSWORD_EXPIRED";
          } else {
            this.errors.login_error = "INCORRECT_LOGIN";
            this.form_states.login = false;
          }
        })
        .catch(err => {
          this.errors.login_error = err.response.data.error;
          this.errors.error = true;
        });
    },
    checkUserExistsForPasswordless() {
      // Used for passwordless login - checks if user exists before sending login link
      return this.$store
        .dispatch("application/userExists", this.email, "passwordless")
        .then(response => {
          if (response.data.found) {
            return true;
          } else {
            this.errors.login_error = "INCORRECT_LOGIN";
            return false;
          }
        })
        .catch(err => {
          this.errors.login_error = err.response.data.errorCode;
          return false;
        });
    },
    async login() {
      if (this.passwordless_login) {
        if (this.$firebase.auth().isSignInWithEmailLink(window.location.href)) {
          if (this.errors.invalid_token) {
            this.resetErrors();
            await this.sendLoginLink();
            return;
          }
          if (this.errors.incorrect_passwordless_email) {
            window.localStorage.setItem("passwordless_login_email", this.email);
          }
          await this.loginWithEmailLink();
        } else {
          this.resetErrors();
          await this.sendLoginLink();
        }
      } else {
        this.resetErrors();
        await this.loginWithEmailAndPassword();
      }
    },
    async loginWithEmailAndPassword() {
      try {
        await this.$firebase
          .auth()
          .signInWithEmailAndPassword(this.email, this.password);
      } catch (err) {
        if (err.code === "auth/wrong-password") {
          this.checkUserExists();
        } else {
          this.checkUserExists();
          this.form_states.login = false;
        }
      }
    },
    async sendLoginLink() {
      // Check if user exists before sending passwordless login link
      const userExists = await this.checkUserExistsForPasswordless();
      if (userExists) {
        this.$store
          .dispatch("application/sendSignInLinkToEmail", this.email)
          .then(() => {
            this.resetErrors();
            window.localStorage.setItem("passwordless_login_email", this.email);
            this.send_email = true;
          })
          .catch(err => {
            this.errors.login_error = err.response.data.errorCode;
          });
      }
    },
    async loginWithEmailLink() {
      let email = window.localStorage.getItem("passwordless_login_email");
      if (!email) {
        this.errors.incorrect_passwordless_email = true;
        return;
      }

      this.$firebase
        .auth()
        .signInWithEmailLink(email, window.location.href)
        .then(result => {
          this.$store
            .dispatch("user/activate_after_sign_in_link", {
              uid: result.user.uid
            })
            .then(() => {
              window.localStorage.removeItem("passwordless_login_email");
            });
        })
        .catch(error => {
          if (error.code === "auth/invalid-action-code") {
            this.errors.invalid_token = true;
            this.errors.login_error = "INVALID_TOKEN";
          } else {
            this.errors.login_error = "PASSWORDLESS_LOGIN_ERROR";
          }
        });
    },
    async loginWithCustomToken() {
      try {
        await this.$firebase.auth().signInWithCustomToken(this.customToken);
      } catch (err) {
        this.form_states.login = false;
        console.error(err);
      }
    },
    getErrorMessage() {
      if (this.errors.login_error === "") return "";
      return this.$t(`errors.login.${this.errors.login_error}`);
    }
  },
  watch: {
    email(n) {
      this.form_states.email = n && this.validateEmail(n);
    },
    password(n) {
      this.form_states.password = n && this.validatePassword(n);
    },
    errors: {
      handler(n) {
        this.has_error = Object.values(n).some(
          value => value === true || value !== ""
        );
      },
      deep: true
    }
  }
};
</script>
